import styled from 'styled-components'
import { useState } from 'react'
import { useErrorHandlerContext, useOpeningElementsControllerContext, useUserLanguage, useListNavigationStateContext } from '@/customHooks'
import { FlattenedTenantData } from '@/custom-types'
import { capitalizeFirstLetter } from '@/helpers'
import { GetTenantsQuery, TenantFilterInput, useDeleteTenantMutation, useGetTenantsQuery } from '@/graphql/generated'
import { ButtonWithDropDownMenu } from '../molecules'
import { useNavigate } from 'react-router'
import { pathURL } from '@/components/constants/urls'
import { Col, Row } from 'react-grid-system'
import { CenterScreenModal, LoadingSpinner } from '../atoms'
import { ConfirmationDialog } from '../organism'
import textsData from '@/textData/TenantListTexts.json'

type TenantData = NonNullable<GetTenantsQuery['tenants']>['items'][number]

type TenantListTableProps = {
  filters: TenantFilterInput;
}

const StyledGridTable = styled.div`
  padding-top: ${({ theme }) => theme.spacing.lg} ;
  padding-bottom: ${({ theme }) => theme.spacing.lg} ;
  border-radius: 12px;
  display: grid;
  grid-template-columns: 1fr 1fr 2fr 64px;
`;

const StyledGridHeader = styled.div`
  display: contents;
  & > div {
    font-weight: bold;
    text-align: left;
    padding: 15px;
    border-top: 1px solid grey;
    border-bottom: 1px solid gray;
  }
`;

const StyledGridRow = styled.div`
  display: contents;
  
  & > div {
    padding: 15px;
    background-color: ${({ theme }) => theme.colors.light};
    font-size: 14px;
    padding-top: 1rem;
    padding-bottom: 1rem;
    align-self: stretch;
    place-content: center;
    border-bottom: 1px solid lightgray;
  }

  &:nth-of-type(odd) > div {
    background-color: ${({ theme }) => theme.colors.background2};
  }
`;

const tableDataMapper = (data: TenantData[] | undefined): FlattenedTenantData[]  => {
  if (!data || data.length === 0) return []

  return data.map((tenant) => ({
    firstName: tenant.firstName,
    lastName: tenant.lastName,
    phoneNumber: tenant.phoneNumber,
    id: tenant.id,
    email: '',
    street: tenant.address.street ?? '',
    city: tenant.address.city?.name?? '',
    state: tenant.address.state?.name?? '',
  }))
}

export const TenantListTable = ({ filters }: TenantListTableProps) => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const { confirmDialogModalOpen } = useOpeningElementsControllerContext()
  
  const { open: errorHandle } = useErrorHandlerContext()
  const { setListState } = useListNavigationStateContext();
  
  const navigate = useNavigate()
  
  const {data, error, refetch, loading} = useGetTenantsQuery({
    variables: {
      filterInput: filters,
    },
    fetchPolicy: 'network-only',
  })
  
  const [deleteTenant] = useDeleteTenantMutation()
  const [tenantIdToDelete, setTenantIdToDelete] = useState<string | null>(null)

  const tableData = tableDataMapper(data?.tenants?.items)
  
  const confirmDeleteHandle = () => {
    if (!tenantIdToDelete) return

    deleteTenant({ variables: { id: tenantIdToDelete } })
    .then((response) => {
      confirmDialogModalOpen({
        title: texts.deleteConfirmation.title,
        description: `${response.data?.deleteTenant.firstName} ${response.data?.deleteTenant.firstName} ${texts.deleteConfirmation.description}`,
      })
      refetch()
    })
    .catch(errorHandle)
    .finally(() => setTenantIdToDelete(null))
  }

  const onDeleteHandle = (id: string) => {
    setTenantIdToDelete(id)
  }

  const onEditHandle = (id: string) => {
    navigate(`/${pathURL.USER}/${pathURL.TENANT}/${pathURL.CREATE}`, {
      state: {
        tenantId: id,
        readOnly: false,
      }
    })
  }

  const onDetailConsultingHandle = (id: string) => {
    setListState('tenants', {
      currentPage: parseInt(filters.page || '1'),
      filters,
      selectedItemId: id,
    });
    navigate(`/${pathURL.USER}/${pathURL.TENANT}/${pathURL.CREATE}`, {
      state: {
        tenantId: id,
        readOnly: true,
      }
    })
  }

  if (loading) {
    return (
      <Row justify='center'>
        <Col xs='content' >
          <LoadingSpinner text/>
        </Col>
      </Row>
    )
  }

  if (tableData.length === 0) {
    return (
      <Row>
        <Col>
          <span><b>{texts.noData}</b></span>
        </Col>
      </Row>
    )
  }

  if (error) errorHandle(error)

  return (
    <>
      <StyledGridTable>
        {/* Encabezado */}
        <StyledGridHeader>
          <div>{texts.name}</div>
          <div>{texts.phone}</div>
          <div>{texts.location}</div>
          <div />
        </StyledGridHeader>

        {/* Filas dinámicas */}
        {tableData.map((data) => (
          <StyledGridRow key={data.id}>
            <div>{`${capitalizeFirstLetter(data.firstName)} ${capitalizeFirstLetter(data.lastName)}`}</div>
            <div>{data.phoneNumber}</div>
            <div>{`${data.street} ${data.city ? `, ${data.city}` : ''} ${data.state ? `, ${data.state}` : ''}`}</div>
            <div>
              <ButtonWithDropDownMenu
                options={[
                  {item: texts.dropdownMenu.deatils, action: () => onDetailConsultingHandle(data.id)},
                  {item: texts.dropdownMenu.edit, action: () => onEditHandle(data.id)},
                  {item: texts.dropdownMenu.delete, action: () => onDeleteHandle(data.id)},
                ]}
                position='left'
              />
            </div>
          </StyledGridRow>
        ))}
      </StyledGridTable>

      <CenterScreenModal
        isOpen={tenantIdToDelete !== null}
        onClose={() => setTenantIdToDelete(null)}
      >
        <ConfirmationDialog
          title={texts.deletePrompt.title}
          description={texts.deletePrompt.description}
          confirmButton={{
            action: confirmDeleteHandle,
            text: texts.deletePrompt.confirm,
          }}
          cancelButton={{
            action: () => setTenantIdToDelete(null),
            text: texts.deletePrompt.cancel,
          }}
        />
      </CenterScreenModal>
    </>
  );
};
